import { BaseApi } from '../baseApi';
import { APIResponse } from '@/types/api';

export interface VolunteerMetrics {
    total_hours: number;
    approved_hours: number;
    pending_hours: number;
    industry_breakdown: Array<{
        industry: string;
        hours: number;
    }>;
    skills_breakdown: Array<{
        skill: string;
        count: number;
    }>;
    organization_breakdown: Array<{
        organization: string;
        hours: number;
    }>;
}

export interface VolunteerLogEntry {
    id: number;
    opportunity: {
        id: number;
        title: string;
        organization: string;
        organization_id: number;
        required_skills: string[];
        organization_industry: string;
        hours_remaining: number;
        commitment_hours: number;
    };
    hours: number;
    description: string;
    status: string;
    date: string;
    created_at: string;
}

export interface StudentVolunteerResponse {
    logs: VolunteerLogEntry[];
    metrics: VolunteerMetrics;
}

export interface TeacherVolunteerResponse {
    // TODO: Implement teacher response structure
    student_metrics: Array<{
        student_id: number;
        student_name: string;
        total_hours: number;
        approved_hours: number;
        pending_hours: number;
    }>;
    class_metrics: VolunteerMetrics;
    pending_approvals: VolunteerLogEntry[];
}

export interface VolunteerOpportunity {
    id: number;
    title: string;
    organization: string;
    organization_id: number;
    required_skills: string[];
    organization_industry: string;
    hours_remaining: number;
    commitment_hours: number;
}

export interface CreateVolunteerLogRequest {
    opportunity_id: number;
    hours: number;
    date: string;
    description: string;
}

export interface UpdateVolunteerStatusRequest {
    status: string;
}

export interface VolunteerResponse extends APIResponse<VolunteerLogEntry[]> {
    metrics: VolunteerMetrics;
}

export class VolunteerApi extends BaseApi {
    private readonly BASE_PATH = '/volunteer-logs';

    async getLogs(params?: { page?: number; limit?: number }): Promise<VolunteerResponse> {
        return this.makeGet<VolunteerResponse>(this.BASE_PATH, { params });
    }

    async getAvailableOpportunities(): Promise<APIResponse<VolunteerOpportunity[]>> {
        return this.makeGet<APIResponse<VolunteerOpportunity[]>>(`${this.BASE_PATH}/opportunities`);
    }

    async create(data: CreateVolunteerLogRequest): Promise<APIResponse<VolunteerLogEntry>> {
        return this.makePost<APIResponse<VolunteerLogEntry>>(this.BASE_PATH, data);
    }

    async updateStatus(id: number, data: UpdateVolunteerStatusRequest): Promise<APIResponse<VolunteerLogEntry>> {
        return this.makePut<APIResponse<VolunteerLogEntry>>(`${this.BASE_PATH}/${id}/status`, data);
    }

    async getLogDetail(id: number): Promise<APIResponse<VolunteerLogEntry>> {
        return this.makeGet<APIResponse<VolunteerLogEntry>>(`${this.BASE_PATH}/${id}`);
    }

    async deleteLog(id: number): Promise<APIResponse<void>> {
        return this.makeDelete<APIResponse<void>>(`${this.BASE_PATH}/${id}`);
    }

    async update(logId: number, data: { hours: number; description: string }): Promise<APIResponse<VolunteerLogEntry>> {
        return this.makePut<APIResponse<VolunteerLogEntry>>(`${this.BASE_PATH}/${logId}`, data);
    }

    async delete(logId: number): Promise<APIResponse<void>> {
        return this.makeDelete<APIResponse<void>>(`${this.BASE_PATH}/${logId}`);
    }
}